import React from 'react';
import SEO from './SEO';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Terms of Service"
        description="Terms of Service for Ahobilam.com — Rules and guidelines for using our Vedic rituals website, services, and content."
        keywords="terms of service, ahobilam, vedic rituals, user agreement"
      />
      <div className="max-w-4xl mx-auto prose prose-orange">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service — Ahobilam.com</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: August 2026</p>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            By accessing and using Ahobilam.com ("website"), you agree to comply with and be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use our website or services. These terms apply to all visitors, 
            users, and others who access or use the website.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">2. Description of Services</h2>
          <p className="text-gray-700 leading-relaxed">
            Ahobilam.com provides educational and spiritual content including Vedic ritual guides, Dharma Sastram scripture 
            compilations, Tamil Panchangam calculations, Q&A discussion boards, and related religious services. Our services 
            include but are not limited to: Panchangam tools, Shraaddha service coordination, remote Archanai services, 
            marriage garland services, and Vedic knowledge resources.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">3. Content & Scripture Accuracy</h2>
          <p className="text-gray-700 leading-relaxed">
            All authentic Vaideeka texts, Dharma Sastram scriptures, panchangam calculations, and Q&A content published on Ahobilam.com 
            are provided for spiritual and educational enlightenment. While we strive for 100% authenticity and scholarship, content is provided 
            on an "as-is" basis. We do not guarantee that any information on this website is complete, reliable, or error-free. 
            Users should consult with qualified religious scholars for specific ritual guidance.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">4. User-Generated Content & Q&A Board</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Our website features a Q&A discussion board where users may submit questions. By submitting content, you agree to the following:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>All submitted questions are subject to moderation before being published.</li>
            <li>You will not submit offensive, defamatory, or illegal content.</li>
            <li>You will not share other people's personal information without their consent.</li>
            <li>We reserve the right to edit, redact, or remove any user-generated content at our sole discretion.</li>
            <li>By submitting a question, you grant Ahobilam.com a non-exclusive, royalty-free license to display, modify, and distribute your submission as part of our Q&A content.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">5. Intellectual Property</h2>
          <p className="text-gray-700 leading-relaxed">
            The design, layout, web applications, custom algorithms, and curated compilations on Ahobilam.com are protected by copyright 
            and intellectual property laws. You may read, study, and share links for personal spiritual use. You may not reproduce, 
            distribute, or create derivative works from our proprietary content without prior written permission.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">6. Advertising</h2>
          <p className="text-gray-700 leading-relaxed">
            This website displays advertisements served by Google AdSense and potentially other third-party advertising networks. 
            These advertisements help support the free availability of our content and services. By using this website, you acknowledge 
            that third-party advertisers may use cookies and similar technologies to serve relevant advertisements. Please refer to our{' '}
            <a href="/privacy-policy" className="text-orange-600 font-semibold underline">Privacy Policy</a> for more details on cookies and advertising.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">7. Prohibited Activities</h2>
          <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Use the website for any unlawful purpose or to solicit others to unlawful acts.</li>
            <li>Submit false, misleading, or spam content to the Q&A board or any other section.</li>
            <li>Attempt to gain unauthorized access to any part of the website or its systems.</li>
            <li>Scrape, data-mine, or automatically collect content from the website without permission.</li>
            <li>Interfere with or disrupt the website's functionality or security.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">8. Disclaimer of Liability</h2>
          <p className="text-gray-700 leading-relaxed">
            Ahobilam.com shall not be held liable for any indirect, incidental, or consequential damages resulting from the use or inability 
            to use our services, panchangam tools, or scripture readers. The website and its content are provided "as is" and "as available" 
            without warranties of any kind, either express or implied.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">9. External Links</h2>
          <p className="text-gray-700 leading-relaxed">
            Our website may contain links to third-party websites. We have no control over and assume no responsibility for the content, 
            privacy policies, or practices of any third-party websites. Your use of third-party websites is at your own risk.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">10. Changes to These Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            We reserve the right to modify or replace these Terms of Service at any time. Changes will be posted on this page with 
            an updated "Last Updated" date. Your continued use of the website after any changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">11. Contact Information</h2>
          <p className="text-gray-700 leading-relaxed">
            For questions regarding these Terms, please contact us at <strong>svknvs@gmail.com</strong> or visit our{' '}
            <a href="/contact" className="text-orange-600 font-semibold underline">Contact Us Page</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsPage;
